/* =====================================================================
   BundleCableSelector — DOM controller for the shared cable panel
   =====================================================================
   One card per length variant, mirroring BundleAccessorySelector exactly:
   click card = select/deselect, per-card quantity stepper. Backed by a
   BundleAddonCore.AddonLineStore.

   Smart quantity: cables are 1-per-camera, so when a card is selected its
   quantity defaults to the cameras still uncovered (cameraCount minus
   cables already selected across all lengths), never below 1. The host
   page can keep cameraCount live via setCameraCount(n) — the kit
   customizer feeds it base + extra cameras; the camera bundle page leaves
   the default of 1 (identical behavior to the accessory cards).

   A full-row status strip ("3 cables / 5 cameras — 2 more needed") shows
   whenever cables are selected and cameraCount > 1. Advisory only.

   Public API (unchanged — host submit logic depends on it):
     getLines() / getCartItems() / getTotal() / clear()
   New optional API: setCameraCount(n)
   ===================================================================== */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  function BundleCableSelector(root, opts) {
    if (!root) return;
    opts = opts || {};
    var Core = window.BundleAddonCore;
    this.root = root;
    this.store = new Core.AddonLineStore(opts.onChange || function () {});
    this.cameraCount = 1;
    this.statusEl = root.querySelector('[data-cable-status]');
    this._bind();
    this._initQtyButtons();
  }

  BundleCableSelector.prototype._card = function (variant) {
    return this.root.querySelector('[data-cable-card][data-variant="' + variant + '"]');
  };

  BundleCableSelector.prototype._totalQty = function () {
    return this.store.getLines().reduce(function (sum, l) { return sum + l.quantity; }, 0);
  };

  BundleCableSelector.prototype._bind = function () {
    var self = this;
    this.root.addEventListener('change', function (ev) {
      var input = ev.target.closest('[data-cable-input]');
      if (input) self._toggle(input);
    });
    this.root.addEventListener('click', function (ev) {
      var qtyBtn = ev.target.closest('[data-cable-qty]');
      if (qtyBtn) { ev.preventDefault(); ev.stopPropagation(); self._qty(qtyBtn); }
    });
  };

  BundleCableSelector.prototype._toggle = function (input) {
    var card = input.closest('[data-cable-card]');
    if (!card) return;
    var variant = parseInt(card.dataset.variant, 10);
    var price = parseInt(card.dataset.price, 10) || 0;
    var label = card.dataset.label || '';
    if (input.checked) {
      // Smart default: cover the cameras that don't have a cable yet.
      var remaining = this.cameraCount - this._totalQty();
      var qty = Math.max(1, remaining);
      this.store.upsert({ id: variant, quantity: qty, price: price, label: label });
    } else {
      this.store.remove(variant);
    }
    this._repaintQty(variant);
    this._repaintStatus();
  };

  // Server markup ships the steppers disabled (no-JS safety); once JS is
  // live they stay clickable even when the card is unselected, so a tap on
  // +/- auto-selects the card instead of dying silently (Clarity showed
  // shoppers repeatedly tapping the dead buttons).
  BundleCableSelector.prototype._initQtyButtons = function () {
    var self = this;
    this.root.querySelectorAll('[data-cable-card]').forEach(function (card) {
      self._repaintQty(parseInt(card.dataset.variant, 10));
    });
  };

  BundleCableSelector.prototype._qty = function (button) {
    if (button.disabled) return;
    var variant = parseInt(button.dataset.variant, 10);
    if (!this.store.has(variant)) {
      // "+" on an unselected card selects it at qty 1 (honest +1, no smart
      // jump — that surprise is what confused shoppers). Card click still
      // smart-fills via _toggle. "−" on 0 does nothing.
      if (button.dataset.action !== 'inc') return;
      var idleCard = this._card(variant);
      var input = idleCard && idleCard.querySelector('[data-cable-input]');
      if (input && !input.checked) {
        input.checked = true;
        this.store.upsert({
          id: variant,
          quantity: 1,
          price: parseInt(idleCard.dataset.price, 10) || 0,
          label: idleCard.dataset.label || ''
        });
        this._repaintQty(variant);
        this._repaintStatus();
      }
      return;
    }
    var cur = this.store.getQty(variant);
    var next = button.dataset.action === 'inc' ? cur + 1 : cur - 1;
    if (next <= 0) {
      // Stepping down to 0 deselects the card — 0 means "not in the kit".
      var offCard = this._card(variant);
      var offInput = offCard && offCard.querySelector('[data-cable-input]');
      if (offInput) offInput.checked = false;
      this.store.remove(variant);
      this._repaintQty(variant);
      this._repaintStatus();
      return;
    }
    var card = this._card(variant);
    this.store.upsert({
      id: variant,
      quantity: next,
      price: card ? (parseInt(card.dataset.price, 10) || 0) : 0,
      label: card ? (card.dataset.label || '') : ''
    });
    var valEl = this.root.querySelector('[data-cable-qty-val][data-variant="' + variant + '"]');
    if (valEl) valEl.textContent = String(next);
    this._repaintStatus();
  };

  BundleCableSelector.prototype._repaintQty = function (variant) {
    var wrap = this.root.querySelector('[data-cable-qty-wrap][data-variant="' + variant + '"]');
    if (!wrap) return;
    var on = this.store.has(variant);
    wrap.querySelectorAll('[data-cable-qty]').forEach(function (b) {
      b.disabled = false;
      b.classList.toggle('is-idle', !on);
    });
    var valEl = wrap.querySelector('[data-cable-qty-val]');
    if (valEl) valEl.textContent = on ? String(this.store.getQty(variant)) : '0';
    var card = this._card(variant);
    if (card) {
      var label = card.querySelector('.pbundle__opt');
      if (label) label.classList.toggle('is-selected', on);
    }
  };

  BundleCableSelector.prototype._repaintStatus = function () {
    if (!this.statusEl) return;
    var total = this._totalQty();
    if (total === 0 || this.cameraCount <= 1) {
      this.statusEl.hidden = true;
      this.statusEl.classList.remove('is-short');
      this.statusEl.classList.remove('is-ok');
      return;
    }
    var text;
    var short = total < this.cameraCount;
    if (short) {
      text = 'Tip: one cable per camera — ' + total + ' of ' + this.cameraCount + ' covered';
    } else {
      text = total + ' cables / ' + this.cameraCount + ' cameras ✓';
    }
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle('is-short', short);
    this.statusEl.classList.toggle('is-ok', !short);
    this.statusEl.hidden = false;
  };

  // Host page keeps this live (kit customizer: base + extra cameras).
  BundleCableSelector.prototype.setCameraCount = function (n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (n === this.cameraCount) return;
    this.cameraCount = n;
    this._repaintStatus();
  };

  BundleCableSelector.prototype.clear = function () {
    this.store.clear();
    var self = this;
    this.root.querySelectorAll('[data-cable-input]').forEach(function (i) { i.checked = false; });
    this.root.querySelectorAll('[data-cable-card]').forEach(function (card) {
      self._repaintQty(parseInt(card.dataset.variant, 10));
    });
    this._repaintStatus();
  };

  BundleCableSelector.prototype.getLines = function () { return this.store.getLines(); };
  BundleCableSelector.prototype.getCartItems = function () { return this.store.getCartItems(); };
  BundleCableSelector.prototype.getTotal = function () { return this.store.getTotal(); };

  window.BundleCableSelector = BundleCableSelector;
})();
